"use client"

import { ChevronLeft, ChevronRight, ChevronsUpDown, Check } from "lucide-react"
import React from "react"
import { DayPicker, DayPickerProps, DropdownOption, DropdownProps } from "react-day-picker"

import { Button, buttonVariants } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils"

export type CalendarProps = DayPickerProps & {
  /**
   * In the year view, the number of years to display at once.
   * @default 12
   */
  yearRange?: number

  /**
   * Wether to show the year switcher in the caption.
   * @default true
   */
  showYearSwitcher?: boolean

  monthsClassName?: string
  monthCaptionClassName?: string
  weekdaysClassName?: string
  weekdayClassName?: string
  monthClassName?: string
  captionClassName?: string
  dropdownClassName?: string
  buttonNextClassName?: string
  buttonPreviousClassName?: string
  navClassName?: string
  monthGridClassName?: string
  weekClassName?: string
  dayClassName?: string
  dayButtonClassName?: string
  rangeStartClassName?: string
  rangeEndClassName?: string
  selectedClassName?: string
  todayClassName?: string
  outsideClassName?: string
  disabledClassName?: string
  rangeMiddleClassName?: string
  hiddenClassName?: string
}

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}: CalendarProps) {

  const _monthsClassName = cn("relative flex", props.monthsClassName)
  const _monthCaptionClassName = cn(
    "relative mx-0 flex h-7 items-center justify-center",
    props.monthCaptionClassName
  )
  const _weekdaysClassName = cn("flex flex-row", props.weekdaysClassName)
  const _weekdayClassName = cn(
    "w-8 text-xs font-normal text-muted-foreground",
    props.weekdayClassName
  )
  const _monthClassName = cn("w-full px-1", props.monthClassName)
  const _captionClassName = cn(
    "relative flex items-center justify-center pt-1",
    props.captionClassName
  )
  const _captionLabelClassName = "hidden"

  const _dropdownClassName =
    cn(
      "truncate text-sm font-medium border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground shadow-sm rounded-md p-[2px]",
      props.dropdownClassName
    )

  const _dropdownsClassName = 'flex gap-1'

  const buttonNavClassName = buttonVariants({
    variant: "outline",
    className:
      "absolute h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 z-[99]",
  })
  const _buttonNextClassName = cn(
    buttonNavClassName,
    "-right-1",
    props.buttonNextClassName
  )
  const _buttonPreviousClassName = cn(
    buttonNavClassName,
    "-left-1",
    props.buttonPreviousClassName
  )
  const _navClassName = cn("flex items-start", props.navClassName)
  const _monthGridClassName = cn("mx-auto mt-4", props.monthGridClassName)
  const _weekClassName = cn("mt-2 flex w-max items-start", props.weekClassName)
  const _dayClassName = cn(
    "flex size-8 flex-1 items-center justify-center p-0 text-sm",
    props.dayClassName
  )
  const _dayButtonClassName = cn(
    buttonVariants({variant: "ghost"}),
    "size-8 rounded-md p-0 font-normal text-xs transition-none aria-selected:opacity-100",
    props.dayButtonClassName
  )
  const buttonRangeClassName =
    "bg-accent [&>button]:bg-primary [&>button]:text-primary-foreground [&>button]:hover:bg-primary [&>button]:hover:text-primary-foreground"
  const _rangeStartClassName = cn(
    buttonRangeClassName,
    "day-range-start rounded-s-md",
    props.rangeStartClassName
  )
  const _rangeEndClassName = cn(
    buttonRangeClassName,
    "day-range-end rounded-e-md",
    props.rangeEndClassName
  )
  const _rangeMiddleClassName = cn(
    "bg-blue-200/50 !text-foreground [&>button]:bg-transparent [&>button]:!text-foreground [&>button]:hover:bg-transparent [&>button]:hover:!text-foreground",
    props.rangeMiddleClassName
  )
  const _selectedClassName = cn(
    "[&>button]:bg-primary [&>button]:text-primary-foreground [&>button]:hover:bg-primary [&>button]:hover:text-primary-foreground",
    props.selectedClassName
  )
  const _todayClassName = cn(
    "[&>button]:bg-accent [&>button]:text-accent-foreground",
    props.todayClassName
  )
  const _outsideClassName = cn(
    "day-outside text-muted-foreground opacity-50 aria-selected:bg-accent/50 aria-selected:text-muted-foreground aria-selected:opacity-30",
    props.outsideClassName
  )
  const _disabledClassName = cn(
    "text-muted-foreground opacity-50",
    props.disabledClassName
  )
  const _hiddenClassName = cn("invisible flex-1", props.hiddenClassName)


  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn("p-0", className)}
      classNames={{
        months: _monthsClassName,
        month_caption: _monthCaptionClassName,
        caption_label: _captionLabelClassName,
        dropdowns: _dropdownsClassName,
        dropdown: _dropdownClassName,
        months_dropdown: "w-25",
        nav: _navClassName,
        button_previous: _buttonPreviousClassName,
        button_next: _buttonNextClassName,
        weekdays: _weekdaysClassName,
        weekday: _weekdayClassName,
        month: _monthClassName,
        caption: _captionClassName,
        month_grid: _monthGridClassName,
        week: _weekClassName,
        day: _dayClassName,
        day_button: _dayButtonClassName,
        range_start: _rangeStartClassName,
        range_middle: _rangeMiddleClassName,
        range_end: _rangeEndClassName,
        selected: _selectedClassName,
        today: _todayClassName,
        outside: _outsideClassName,
        disabled: _disabledClassName,
        hidden: _hiddenClassName,
        ...classNames,
      }}
      components={{
        Chevron: ({orientation}) => {
          const Icon = orientation === "left" ? ChevronLeft : ChevronRight
          return <Icon className="size-4"/>
        },
        Dropdown: (props: DropdownProps) => {
          const selected = props.options?.find(o => o.value === props.value)
          const handleChange = (option: DropdownOption) => {
            if (props.onChange) {
              const _event = {
                target: {
                  value:option.value
                },
              } as unknown as React.ChangeEvent<HTMLSelectElement>
              props.onChange(_event);
            }
          }
          return (
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild >
                <Button size="sm" variant={'outline'} className={cn("data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground px-2", props.className)}>
                  {selected?.label}
                  <ChevronsUpDown className="ml-auto size-4"/>
                </Button>
              </DropdownMenuTrigger>

              <DropdownMenuContent
                className="w-[--radix-dropdown-menu-trigger-width] rounded-md overflow-scroll max-h-[50vh]"
                side='top'
                align="end"
                sideOffset={4}
              >
                {props.options?.map(option => (
                  <DropdownMenuItem
                    key={option.label}
                    onClick={() => handleChange(option)}
                    className={`hover:cursor-pointer ${option.label === selected?.label ? 'text-secondary' : ''}`}
                  >
                    <span className={'flex items-center gap-2'}>{option.label} {option.label === selected?.label && <Check size={16} />}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )
        }
      }}
      {...props}
    />
  )
}
Calendar.displayName = "Calendar"

export { Calendar }
